/**
 * useDesignerVariantSelection — Shared variant selection handler for designer nodes
 *
 * Updates ``out_image.images.main.picked_index`` (canonical, per RFC-0707)
 * when the user picks a different variant from the history.
 *
 * The picked_index write goes directly to the existing YDoc Y.Map path instead
 * of sending a rendered out_image object through canvas.updateNodeData. Output
 * media history is backend-authored; the frontend may patch UI-only leaves
 * such as picked_index, but must not rewrite the whole field.
 *
 * Sprint 3a: Designer Nodes — Shared Foundation
 */

import { useCallback } from 'react'
import * as Y from 'yjs'
import { useCanvasOptional } from '../../CanvasContext'
import type { HistoryEntry } from './types'

interface UseDesignerVariantSelectionOptions {
  nodeId: string
  nodeType: 'characterDesigner' | 'locationDesigner' | 'objectDesigner'
  historyEntries: HistoryEntry[]
  /** The data field name for the structured output (e.g., 'out_image') */
  outputFieldName: string
}

/**
 * Returns a handleHistorySelect callback that:
 * 1. Verifies the selected history entry resolves to a local_path
 * 2. Walks the YDoc to ``out_image.images.main`` and sets ``picked_index``
 * 3. Leaves asset cache keys untouched; selection changes do not mutate bytes
 *
 * The YDoc write uses a non-"frontend" transaction origin so the Yjs observer
 * propagates the picked_index change back into ReactFlow data without changing
 * any asset cache keys.
 */
export function useDesignerVariantSelection({
  nodeId,
  nodeType,
  historyEntries,
  outputFieldName,
}: UseDesignerVariantSelectionOptions): (index: number) => void {
  const canvas = useCanvasOptional()

  return useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      if (!entry?.result?.local_path) return

      // Path-based write directly into the existing Y.Map at
      // out_image.images.main.picked_index. Single key mutation; the
      // history Y.Array sibling is left untouched.
      const ynode = canvas?.getYNode?.(nodeId)
      if (ynode) {
        const outImage = ynode.get(outputFieldName)
        const images = outImage instanceof Y.Map ? outImage.get('images') : undefined
        const main = images instanceof Y.Map ? images.get('main') : undefined
        if (main instanceof Y.Map && main.get('picked_index') !== index) {
          const doc = ynode.doc
          if (doc) {
            doc.transact(() => {
              main.set('picked_index', index)
            }, 'variant-selection')
          } else {
            main.set('picked_index', index)
          }
        }
      }

      void nodeType
    },
    [nodeId, nodeType, historyEntries, outputFieldName, canvas]
  )
}
